import { useEffect, useState, useCallback, useRef } from 'react';
import { cacheService } from '../services/cacheService';
import { performanceService } from '../services/performanceService';
import { persianFontOptimizer } from '../services/persianFontOptimizer';

export interface PerformanceMetrics {
  cacheStats: {
    hits: number;
    misses: number;
    size: number;
    hitRate: number;
  };
  fontLoadingStatus: Record<string, boolean>;
  memoryUsage?: {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
  };
  renderMetrics: Record<string, any>;
}

export interface PerformanceOptimizationConfig {
  enableCaching: boolean;
  enableFontOptimization: boolean;
  enablePerformanceTracking: boolean;
  cacheSize: number;
  cacheTTL: number;
}

export function usePerformanceOptimization(config?: Partial<PerformanceOptimizationConfig>) {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    cacheStats: { hits: 0, misses: 0, size: 0, hitRate: 0 },
    fontLoadingStatus: {},
    renderMetrics: {}
  });

  const [isOptimized, setIsOptimized] = useState(false);
  const [loading, setLoading] = useState(false); // Start with false to prevent blocking
  const metricsUpdateInterval = useRef<NodeJS.Timeout>();

  const defaultConfig: PerformanceOptimizationConfig = {
    enableCaching: true,
    enableFontOptimization: true,
    enablePerformanceTracking: true,
    cacheSize: 200,
    cacheTTL: 5 * 60 * 1000, // 5 minutes
    ...config
  };

  /**
   * Initialize performance optimization
   */
  const initializeOptimization = useCallback(async () => {
    // Don't show loading for performance optimization to avoid blocking UI
    try {
      // Initialize Persian font optimization in background
      if (defaultConfig.enableFontOptimization) {
        persianFontOptimizer.preloadCriticalFonts().catch(console.warn);
        persianFontOptimizer.optimizeTextRendering();
      }

      // Initialize performance tracking in background
      if (defaultConfig.enablePerformanceTracking) {
        performanceService.optimizePersianFontLoading().catch(console.warn);
        performanceService.optimizeImages();
      }

      setIsOptimized(true);
    } catch (error) {
      console.warn('Performance optimization failed:', error);
      setIsOptimized(true);
    }
  }, [defaultConfig]);

  /**
   * Update performance metrics
   */
  const updateMetrics = useCallback(() => {
    const newMetrics: PerformanceMetrics = {
      cacheStats: cacheService.getStats(),
      fontLoadingStatus: persianFontOptimizer.getFontLoadingStatus(),
      memoryUsage: performanceService.getMemoryUsage(),
      renderMetrics: performanceService.getMetrics()
    };

    setMetrics(newMetrics);
  }, []);

  /**
   * Clear cache
   */
  const clearCache = useCallback(() => {
    cacheService.clear();
    updateMetrics();
  }, [updateMetrics]);

  /**
   * Preload fonts for content
   */
  const preloadFontsForContent = useCallback(async (content: string) => {
    if (defaultConfig.enableFontOptimization) {
      await persianFontOptimizer.preloadFontsForContent(content);
      updateMetrics();
    }
  }, [defaultConfig.enableFontOptimization, updateMetrics]);

  /**
   * Measure component performance
   */
  const measureComponentRender = useCallback(<T>(
    componentName: string,
    renderFn: () => T
  ): T => {
    if (!defaultConfig.enablePerformanceTracking) {
      return renderFn();
    }

    return performanceService.measureComponentRender(componentName, renderFn);
  }, [defaultConfig.enablePerformanceTracking]);

  /**
   * Measure API call performance
   */
  const measureApiCall = useCallback(async <T>(
    apiName: string,
    apiCall: () => Promise<T>
  ): Promise<T> => {
    if (!defaultConfig.enablePerformanceTracking) {
      return apiCall();
    }

    return performanceService.measureApiCall(apiName, apiCall);
  }, [defaultConfig.enablePerformanceTracking]);

  /**
   * Get performance recommendations
   */
  const getPerformanceRecommendations = useCallback(() => {
    const recommendations: string[] = [];
    
    // Cache recommendations
    if (metrics.cacheStats.hitRate < 0.5) {
      recommendations.push('Consider increasing cache TTL or size for better cache hit rate');
    }
    
    if (metrics.cacheStats.size > defaultConfig.cacheSize * 0.9) {
      recommendations.push('Cache is nearly full, consider increasing cache size');
    }

    // Memory recommendations
    if (metrics.memoryUsage) {
      const memoryUsagePercent = metrics.memoryUsage.usedJSHeapSize / metrics.memoryUsage.jsHeapSizeLimit;
      if (memoryUsagePercent > 0.8) {
        recommendations.push('High memory usage detected, consider optimizing component renders');
      }
    }

    // Font loading recommendations
    const fontStatus = metrics.fontLoadingStatus;
    const unloadedFonts = Object.entries(fontStatus).filter(([_, loaded]) => !loaded);
    if (unloadedFonts.length > 0) {
      recommendations.push(`Some fonts failed to load: ${unloadedFonts.map(([font]) => font).join(', ')}`);
    }

    return recommendations;
  }, [metrics, defaultConfig.cacheSize]);

  /**
   * Optimize for mobile devices
   */
  const optimizeForMobile = useCallback(() => {
    // Keep in sync with MobileResponsive MOBILE_LAYOUT_MAX_WIDTH_PX / Tailwind sm (640px).
    const isMobile = window.innerWidth < 640;
    
    if (isMobile) {
      // Reduce cache size on mobile
      cacheService.clear();
      
      // Apply mobile-specific optimizations
      const mobileCSS = `
        .persian-text {
          text-rendering: optimizeSpeed;
          -webkit-font-smoothing: subpixel-antialiased;
        }
        
        img {
          image-rendering: optimizeSpeed;
        }
      `;
      
      // Temporarily disabled to prevent PostCSS errors
      // const style = document.createElement('style');
      // style.textContent = mobileCSS;
      // document.head.appendChild(style);
    }
  }, []);

  // Initialize optimization on mount
  useEffect(() => {
    initializeOptimization();
    
    return () => {
      if (metricsUpdateInterval.current) {
        clearInterval(metricsUpdateInterval.current);
      }
    };
  }, [initializeOptimization]);

  // Set up metrics update interval
  useEffect(() => {
    if (isOptimized && defaultConfig.enablePerformanceTracking) {
      metricsUpdateInterval.current = setInterval(updateMetrics, 30000); // Update every 30 seconds
      
      // Initial metrics update
      updateMetrics();
    }

    return () => {
      if (metricsUpdateInterval.current) {
        clearInterval(metricsUpdateInterval.current);
      }
    };
  }, [isOptimized, defaultConfig.enablePerformanceTracking, updateMetrics]);

  // Optimize for mobile on resize
  useEffect(() => {
    const handleResize = () => {
      optimizeForMobile();
    };

    window.addEventListener('resize', handleResize);
    optimizeForMobile(); // Initial check

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [optimizeForMobile]);

  return {
    metrics,
    isOptimized,
    loading,
    clearCache,
    preloadFontsForContent,
    measureComponentRender,
    measureApiCall,
    getPerformanceRecommendations,
    updateMetrics,
    optimizeForMobile
  };
}

export default usePerformanceOptimization;